import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BottomNav from '../components/BottomNav';
import UserHeader from '../components/UserHeader';
import { trackPageView, trackClick } from '../utils/analytics';
import {
  createConnectionCode,
  getConnectionStatus,
  connectionQrImageUrl,
  unlinkConnection,
} from '../utils/connectionCodes';
import { getChildrenForUser, getParentsForUser, Connection } from '../utils/parentStudentFirestore';
import cloudsImg from '../assets/webp/clouds.webp';
import { DEEP_LINK_BASE_URL } from '../utils/common';

const BLUE = '#3B7BF4';
const CARD_BG = '#fffbe9';
const DASH = '#AEB9CC';
const NAVY = '#1E2A4A';
const TEXT_DARK = '#424656';
const TEXT_GRAY = '#6B7280';
const BORDER = '#E5E7EB';
const GREEN_WA = '#25D366';
const GREEN = '#22C55E';
const GOLD = '#F5C842';
const RED = '#EF4444';

const POLL_INTERVAL_MS = 4000;

type Phase = 'loading' | 'ready' | 'error';

interface LoggedInUser {
  email?: string;
  displayName?: string;
  name?: string;
  userId?: string;
  uid?: string;
}

function readLoggedInUser(): LoggedInUser | null {
  try {
    const raw = localStorage.getItem('loggedInUser');
    return raw ? (JSON.parse(raw) as LoggedInUser) : null;
  } catch {
    return null;
  }
}

const ConnectChildPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [phase, setPhase] = useState<Phase>('loading');
  const [code, setCode] = useState<string>('');
  const [connections, setConnections] = useState<Connection[]>([]);
  const [showQr, setShowQr] = useState(false);
  const [copied, setCopied] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Redirect old-format QR scans (/connect-child?code=X) to the root deep-link handler
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    if (code) {
      navigate(`/?connectionId=${encodeURIComponent(code)}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const user = useMemo(() => readLoggedInUser(), []);
  const childEmail = (user?.email || '').trim();
  const childName = user?.displayName || user?.name || (childEmail ? childEmail.split('@')[0] : '');

  const allocateCode = async () => {
    const res = await createConnectionCode({
      childEmail,
      childName,
      userId: user?.userId ?? user?.uid ?? null,
    });
    if (res.success && res.code) {
      setCode(res.code);
      return res.code;
    }
    return '';
  };

  const refreshConnections = useCallback(async () => {
    try {
      const [asChild, asParent] = await Promise.all([
        getParentsForUser(childEmail).catch(() => [] as Connection[]),
        getChildrenForUser(childEmail).catch(() => [] as Connection[]),
      ]);
      setConnections(asChild.length > 0 ? asChild : asParent);
    } catch (err) {
      console.error('refreshConnections error:', err);
    }
  }, [childEmail]);

  useEffect(() => {
    trackPageView('ChildConnect');
    let cancelled = false;

    (async () => {
      if (!childEmail) {
        setErrorMsg(
          t(
            'connect_err_login_create',
            'You need to sign in to create a connection code.',
          ) as string,
        );
        setPhase('error');
        return;
      }
      try {
        const [asChild, asParent, allocated] = await Promise.all([
          getParentsForUser(childEmail).catch(() => [] as Connection[]),
          getChildrenForUser(childEmail).catch(() => [] as Connection[]),
          allocateCode(),
        ]);
        const list = asChild.length > 0 ? asChild : asParent;
        if (cancelled) return;
        setConnections(list);
        setPhase(allocated || list.length > 0 ? 'ready' : 'error');
        if (!allocated && list.length === 0)
          setErrorMsg(
            t(
              'connect_err_create_failed',
              "We couldn't create a connection code. Please try again later.",
            ) as string,
          );
      } catch (err) {
        if (cancelled) return;
        console.error('ChildConnectPage init error:', err);
        setErrorMsg(t('connect_err_failed', 'Connection failed. Please try again.') as string);
        setPhase('error');
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showQr || !code || phase !== 'ready') return;

    const tick = async () => {
      const status = await getConnectionStatus(code);
      if (status.success && status.status === 'linked') {
        await refreshConnections();
        await allocateCode();
      }
    };
    pollRef.current = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showQr, code, phase]);

  const deepLink = `${DEEP_LINK_BASE_URL}?connectionId=${code}`;
  const shareText = t('connect_share_text', {
    code,
    deepLink,
    defaultValue:
      'Join me on Kol Torah to follow my progress. My connection code: {{code}}\nConnect directly: {{deepLink}}',
  }) as string;

  const handleWhatsApp = () => {
    trackClick('Share WhatsApp Child', 'ChildConnect');
    const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCopyLink = async () => {
    trackClick('Copy Link Child', 'ChildConnect');
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        backgroundImage: `url(${cloudsImg})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% auto',
        backgroundPosition: 'top center',
        fontFamily: "var(--kol-font-sans, 'Heebo', sans-serif)",
        direction: 'rtl',
      }}
    >
      <UserHeader />

      {/* Scrollable area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 18px 110px', boxSizing: 'border-box' }}>
        {/* Page header */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <h1
            style={{
              fontSize: 28,
              fontWeight: 800,
              color: NAVY,
              margin: '0 0 6px',
            }}
          >
            {t('family_page_title', 'המשפחה שלי')}
          </h1>
          <p style={{ fontSize: 14, color: TEXT_GRAY, margin: 0, lineHeight: 1.5 }}>
            {t('family_page_subtitle', 'חבר הורה או ילד כדי לעקוב אחר ההתקדמות')}
          </p>
        </div>

        {phase === 'loading' && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: TEXT_GRAY }}>
            <Spinner />
            <div style={{ marginTop: 14, fontSize: 15 }}>{t('connect_loading', 'Loading...')}</div>
          </div>
        )}

        {phase === 'error' && (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>😕</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: TEXT_DARK, marginBottom: 8 }}>
              {t('connect_error_title', 'Something went wrong')}
            </div>
            <div style={{ fontSize: 14, color: TEXT_GRAY, lineHeight: 1.6 }}>{errorMsg}</div>
          </div>
        )}

        {phase === 'ready' && (
          <>
            {/* Connected children */}
            {connections.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_GRAY, marginBottom: 10 }}>
                  {t('connect_connected_children', 'Connected children')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {connections.map((c, i) => (
                    <ConnectionCard
                      key={c.email}
                      connection={c}
                      index={i}
                      viewerEmail={childEmail}
                      onPress={() => {}}
                      onDisconnected={refreshConnections}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Main invite card */}
            <div
              style={{
                background: CARD_BG,
                borderRadius: 20,
                boxShadow: '0px 4px 20px 1px rgba(0,0,0,0.10)',
                padding: '16px 16px 0',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <h2
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: TEXT_DARK,
                  margin: '0 0 6px',
                  textAlign: 'center',
                }}
              >
                {t('invite_parent_title', 'הזמן הורה')}
              </h2>
              <p
                style={{
                  fontSize: 13,
                  color: TEXT_GRAY,
                  textAlign: 'center',
                  margin: '0 0 14px',
                  lineHeight: 1.5,
                  maxWidth: 260,
                }}
              >
                {t(
                  'invite_parent_desc',
                  'שלח את הקישור להורה שלך והוא יוכל להתחבר ולעקוב אחרי ההתקדמות שלך',
                )}
              </p>

              {/* WhatsApp button */}
              <button
                type="button"
                onClick={handleWhatsApp}
                style={{
                  width: '100%',
                  padding: '11px 16px',
                  background: GREEN_WA,
                  border: 'none',
                  borderRadius: 50,
                  fontSize: 15,
                  fontWeight: 700,
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <WhatsAppIcon />
                {t('invite_whatsapp', 'שלח וואטסאפ')}
              </button>

              {/* Copy link button */}
              <button
                type="button"
                onClick={handleCopyLink}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: 'white',
                  border: `1.5px solid ${BORDER}`,
                  borderRadius: 50,
                  fontSize: 15,
                  fontWeight: 600,
                  color: TEXT_DARK,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <LinkIcon />
                {copied ? t('invite_link_copied', 'הועתק ✓') : t('invite_copy_link', 'העתק קישור')}
              </button>

              {/* Show QR button */}
              <button
                type="button"
                onClick={() => {
                  trackClick('Toggle QR Child', 'ChildConnect');
                  setShowQr((v) => !v);
                }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: 'white',
                  border: `1.5px solid ${BORDER}`,
                  borderRadius: 50,
                  fontSize: 15,
                  fontWeight: 600,
                  color: TEXT_DARK,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                <QrIcon />
                {showQr ? t('invite_hide_qr', 'הסתר קוד') : t('invite_show_qr', 'הצג קוד חיבור')}
              </button>

              {/* QR + code — expands inline */}
              {showQr && (
                <div style={{ width: '100%', paddingTop: 14 }}>
                  <div
                    style={{
                      background: '#F4F7FC',
                      border: `1.5px dashed ${DASH}`,
                      borderRadius: 12,
                      padding: '10px 0',
                      textAlign: 'center',
                      marginBottom: 10,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 24,
                        fontWeight: 800,
                        letterSpacing: 8,
                        color: NAVY,
                        fontFamily: 'monospace',
                        direction: 'ltr',
                        display: 'inline-block',
                      }}
                    >
                      {code || '------'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
                    <div
                      style={{
                        background: 'white',
                        padding: 8,
                        borderRadius: 10,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      }}
                    >
                      {code ? (
                        <img
                          src={connectionQrImageUrl(code, 140)}
                          alt="QR code"
                          width={140}
                          height={140}
                          style={{ display: 'block' }}
                        />
                      ) : (
                        <div style={{ width: 140, height: 140 }} />
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Security note */}
              <div
                style={{
                  background: 'rgba(59,111,227,0.08)',
                  borderRadius: 12,
                  padding: '8px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  margin: '8px 0 12px',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <ShieldIcon />
                <span style={{ fontSize: 12, color: TEXT_GRAY, lineHeight: 1.4 }}>
                  {t('invite_security_note', 'הקישור והקוד הם אישיים ובטוחים לשיתוף')}
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const ConnectionCard: React.FC<{
  connection: Connection;
  index: number;
  viewerEmail: string;
  onPress: () => void;
  onDisconnected: () => void | Promise<void>;
}> = ({ connection, index, viewerEmail, onPress, onDisconnected }) => {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const isGold = index % 2 === 1;

  const doDisconnect = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setBusy(true);
    const pEmail = connection.role === 'parent' ? connection.email : viewerEmail;
    const cEmail = connection.role === 'parent' ? viewerEmail : connection.email;
    await unlinkConnection({ parentEmail: pEmail, childEmail: cEmail });
    setBusy(false);
    setConfirming(false);
    await onDisconnected();
  };

  const cardBase: React.CSSProperties = {
    background: 'white',
    borderRadius: 14,
    padding: '12px 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0 1px 6px rgba(20,40,80,0.06)',
    border: `1px solid ${BORDER}`,
    cursor: 'pointer',
  };

  if (confirming) {
    return (
      <div style={{ ...cardBase, cursor: 'default', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={doDisconnect}
            disabled={busy}
            style={{
              border: 'none',
              borderRadius: 10,
              padding: '8px 14px',
              background: RED,
              color: 'white',
              fontWeight: 700,
              fontSize: 13,
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.7 : 1,
            }}
          >
            {busy
              ? t('connect_disconnecting', 'Disconnecting...')
              : t('connect_disconnect', 'Disconnect')}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setConfirming(false);
            }}
            disabled={busy}
            style={{
              border: `1px solid ${BORDER}`,
              borderRadius: 10,
              padding: '8px 14px',
              background: 'white',
              color: TEXT_GRAY,
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            {t('connect_cancel', 'Cancel')}
          </button>
        </div>
        <span style={{ fontSize: 14, fontWeight: 600, color: TEXT_DARK }}>
          {t('connect_disconnect_confirm', {
            name: connection.name,
            defaultValue: 'Disconnect {{name}}?',
          })}
        </span>
      </div>
    );
  }

  return (
    <div style={cardBase} onClick={onPress} role="button">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: '#F0FAF3',
            borderRadius: 999,
            padding: '4px 10px',
          }}
        >
          <span style={{ fontSize: 13, color: GREEN, fontWeight: 600 }}>
            {t('connect_active', 'Active')}
          </span>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: GREEN }} />
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setConfirming(true);
          }}
          aria-label={t('connect_disconnect', 'Disconnect') as string}
          style={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            border: `1px solid ${BORDER}`,
            background: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke={RED}
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ flexShrink: 0, display: 'block' }}
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: TEXT_DARK }}>{connection.name}</span>
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: '50%',
            background: isGold ? GOLD : '#DCE3EF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke={isGold ? TEXT_DARK : '#7C8BA5'}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ flexShrink: 0, display: 'block' }}
          >
            <circle cx="12" cy="8" r="4" />
            <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
          </svg>
        </div>
      </div>
    </div>
  );
};

const WhatsAppIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="white" style={{ flexShrink: 0 }}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.122.552 4.109 1.518 5.834L0 24l6.335-1.505A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.9 0-3.68-.523-5.2-1.435l-.372-.22-3.763.893.948-3.668-.242-.38A9.944 9.944 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
  </svg>
);

const LinkIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const QrIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <path d="M14 14h1v1h-1zM18 14h3v3h-1v-1h-1v1h-1zM14 18h3v3h-1v-1h-2z" />
  </svg>
);

const ShieldIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
    <path
      d="M12 2L3 6v6c0 5.25 3.75 10.15 9 11.25C17.25 22.15 21 17.25 21 12V6l-9-4z"
      fill={BLUE}
      opacity="0.15"
    />
    <path
      d="M12 2L3 6v6c0 5.25 3.75 10.15 9 11.25C17.25 22.15 21 17.25 21 12V6l-9-4z"
      stroke={BLUE}
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M9 12l2 2 4-4"
      stroke={BLUE}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Spinner: React.FC<{ size?: number }> = ({ size = 36 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 50 50"
    style={{ animation: 'ktc-spin 0.9s linear infinite' }}
  >
    <style>{'@keyframes ktc-spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}'}</style>
    <circle cx="25" cy="25" r="20" fill="none" stroke="#E5E7EB" strokeWidth="5" />
    <circle
      cx="25"
      cy="25"
      r="20"
      fill="none"
      stroke={BLUE}
      strokeWidth="5"
      strokeLinecap="round"
      strokeDasharray="90 150"
    />
  </svg>
);

export default ConnectChildPage;
